import type { SocialPostEditableDraft } from "@runcheetah/shared";

export type PostDraftHistory = {
  past: SocialPostEditableDraft[];
  future: SocialPostEditableDraft[];
};

export const EMPTY_POST_DRAFT_HISTORY: PostDraftHistory = {
  past: [],
  future: [],
};

const DEFAULT_HISTORY_LIMIT = 50;

const draftKey = (draft: SocialPostEditableDraft | null): string => JSON.stringify(draft);

export const pushPostDraftHistory = (input: {
  history: PostDraftHistory;
  current: SocialPostEditableDraft | null;
  next: SocialPostEditableDraft;
  limit?: number;
}): PostDraftHistory => {
  if (!input.current || draftKey(input.current) === draftKey(input.next)) {
    return input.history;
  }

  const limit = input.limit ?? DEFAULT_HISTORY_LIMIT;
  const past = [...input.history.past, input.current].slice(-limit);

  return { past, future: [] };
};

export const undoPostDraftHistory = (input: {
  history: PostDraftHistory;
  current: SocialPostEditableDraft | null;
}): { history: PostDraftHistory; draft: SocialPostEditableDraft | null } => {
  const previous = input.history.past.at(-1) ?? null;
  if (!previous) {
    return { history: input.history, draft: input.current };
  }

  return {
    draft: previous,
    history: {
      past: input.history.past.slice(0, -1),
      future: input.current ? [input.current, ...input.history.future] : input.history.future,
    },
  };
};

export const redoPostDraftHistory = (input: {
  history: PostDraftHistory;
  current: SocialPostEditableDraft | null;
}): { history: PostDraftHistory; draft: SocialPostEditableDraft | null } => {
  const next = input.history.future[0] ?? null;
  if (!next) {
    return { history: input.history, draft: input.current };
  }

  return {
    draft: next,
    history: {
      past: input.current ? [...input.history.past, input.current] : input.history.past,
      future: input.history.future.slice(1),
    },
  };
};
